import { useParams } from 'react-router-dom'
import { getConsultation } from '../../api/clinical'
import { getPatient } from '../../api/patients'
import { PrintShell } from '../../components/PrintShell'
import { formatPrintDate, printLabel } from '../../lib/print'
import { useFetch } from '../../hooks/useFetch'
import { SPECIES_LABELS } from '../../types/patients'

export default function PrescriptionPrint() {
  const { id } = useParams<{ id: string }>()
  const { data, loading, error } = useFetch(async () => {
    const consultation = await getConsultation(Number(id))
    return { consultation, patient: await getPatient(consultation.patient_id) }
  }, [id])

  return (
    <PrintShell title={data ? `Prescription — ${data.patient.name}` : 'Prescription'} loading={loading} error={error}>
      {() => {
        const { consultation: c, patient } = data!
        const owner = patient.owners.find(o => o.is_primary) ?? patient.owners[0]
        return (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '2px solid #111', paddingBottom: '16px', marginBottom: '24px' }}>
              <div>
                <h1 style={{ margin: 0, fontSize: '26px' }}>Prescription</h1>
                {c.practice_name && <div style={{ color: '#444', marginTop: '4px' }}>{c.practice_name}</div>}
              </div>
              <div style={{ textAlign: 'right', fontSize: '13px', color: '#444' }}>
                <div><strong>Date:</strong> {formatPrintDate(c.date)}</div>
                {c.veterinarian_name && <div><strong>Vet:</strong> Dr. {c.veterinarian_name}</div>}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '28px' }}>
              <div>
                <div style={printLabel}>Patient</div>
                <div style={{ fontWeight: 'bold' }}>{patient.name}</div>
                <div style={{ color: '#444' }}>{[SPECIES_LABELS[patient.species], patient.breed].filter(Boolean).join(' · ')}</div>
              </div>
              {owner && (
                <div>
                  <div style={printLabel}>Owner</div>
                  <div style={{ fontWeight: 'bold' }}>{owner.first_name} {owner.last_name}</div>
                </div>
              )}
            </div>

            {c.prescriptions.length === 0 ? (
              <p style={{ color: '#888', fontStyle: 'italic' }}>No prescriptions on this consultation.</p>
            ) : c.prescriptions.map(p => (
              <div key={p.id} style={{ borderBottom: '1px solid #ddd', padding: '12px 0' }}>
                <div style={{ fontWeight: 'bold', fontSize: '16px' }}>℞ {p.medication_name}</div>
                <div style={{ color: '#333' }}>
                  {[p.dosage, p.frequency, p.duration && `for ${p.duration}`].filter(Boolean).join(' · ')}
                </div>
                {p.quantity_dispensed && <div style={{ color: '#444' }}>Quantity dispensed: {parseFloat(p.quantity_dispensed)}</div>}
                {p.instructions && <div style={{ marginTop: '4px', whiteSpace: 'pre-wrap' }}>{p.instructions}</div>}
              </div>
            ))}
          </>
        )
      }}
    </PrintShell>
  )
}
